/**
 * useCollaboration.js
 *
 * Sets up a Yjs shared document and a y-websocket provider for a specific
 * rfpId + sectionId pair. Returns the ydoc, provider, and awareness state.
 *
 * FAILSAFE: If the WebSocket server is unavailable, the hook resolves cleanly
 * without crashing – the editor continues to work in normal (non-collab) mode.
 *
 * CLEANUP: Destroys the provider and ydoc on unmount to prevent memory leaks
 * and duplicate WebSocket connections.
 */

import { useEffect, useRef, useState } from 'react';
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';

// User colors pool – each user gets a stable color derived from their userId
const USER_COLORS = [
    '#7C3AED', // violet
    '#2563EB', // blue
    '#059669', // emerald
    '#D97706', // amber
    '#DC2626', // red
    '#DB2777', // pink
    '#0891B2', // cyan
    '#65A30D', // lime
    '#9333EA', // purple
    '#EA580C', // orange
];

/**
 * Returns a stable color for a given user id string.
 */
export function getUserColor(userId) {
    if (!userId) return USER_COLORS[0];
    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
        hash = userId.charCodeAt(i) + ((hash << 5) - hash);
    }
    return USER_COLORS[Math.abs(hash) % USER_COLORS.length];
}

/**
 * Derives user initials from a display name.
 */
export function getUserInitials(name) {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * @param {object} params
 * @param {string} params.rfpId
 * @param {string|number} params.sectionId  – section key or id
 * @param {object} params.user              – { id, name, email }
 * @param {string} params.wsBaseUrl         – e.g. "wss://your-api.com"
 */
export function useCollaboration({ rfpId, sectionId, user, wsBaseUrl, isEditing }) {
    const ydocRef = useRef(null);
    const providerRef = useRef(null);

    const [status, setStatus] = useState('connecting'); // 'connecting' | 'connected' | 'disconnected' | 'unavailable'
    const [collaborators, setCollaborators] = useState([]); // array of awareness user objects
    const [collabReady, setCollabReady] = useState(false);

    // Stable room name per section
    const roomName = rfpId && sectionId ? `rfp-${rfpId}-section-${sectionId}` : null;

    useEffect(() => {
        // If no roomName or no wsBaseUrl, skip setup entirely → non-collab mode
        if (!roomName || !wsBaseUrl || !user) {
            setStatus('unavailable');
            return;
        }

        let destroyed = false;

        try {
            const ydoc = new Y.Doc();
            ydocRef.current = ydoc;

            let wsUrl = wsBaseUrl.trim().replace(/\/$/, ''); // strip trailing slash
            let finalRoomName = roomName;

            // Sanitize malformed protocols like wss://https:// or https://
            if (/^wss?:\/\/https?:\/\//i.test(wsUrl)) {
                wsUrl = wsUrl.replace(/^wss?:\/\/https?:\/\//i, 'wss://');
            } else if (/^https?:\/\//i.test(wsUrl)) {
                wsUrl = wsUrl.replace(/^https?:\/\//i, 'wss://');
            }

            // Dynamically format for backend structured websocket route if needed
            if (wsUrl.includes('/ws/collab')) {
                wsUrl = `${wsUrl}/rfp/${rfpId}/section`;
                finalRoomName = String(sectionId);
            }

            // Extract and sanitize token for the WebSocket connection
            let token = localStorage.getItem('access_token');
            if (token) {
                token = token.replace(/^Bearer\s+/i, '').trim();
            }

            const provider = new WebsocketProvider(wsUrl, finalRoomName, ydoc, {
                connect: true,
                // Reconnect up to 10 times with exponential backoff
                maxBackoffTime: 8000,
                params: {
                    ...(token ? { token } : {}),
                    'ngrok-skip-browser-warning': 'true'
                },
            });
            providerRef.current = provider;

            // Clean up name/email to ensure we show a friendly name, never raw email
            const getCleanName = () => {
                const rawName = user.employee_name || user.full_name || user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim();
                if (rawName) return rawName;

                const email = user.email || 'Anonymous';
                if (email.includes('@')) {
                    const part = email.split('@')[0];
                    return part
                        .split(/[._-]/)
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(' ');
                }
                return email;
            };

            const finalCollabName = getCleanName();
            const userMeta = {
                name: finalCollabName,
                userId: String(user.id || user.email || Math.random()),
                color: getUserColor(String(user.id || user.email || '')),
                initials: getUserInitials(finalCollabName),
            };
            provider.awareness.setLocalStateField('user', userMeta);
            provider.awareness.setLocalStateField('isEditing', !!isEditing);

            // ── Connection status ─────────────────────────────────────────────
            provider.on('status', ({ status: wsStatus }) => {
                if (destroyed) return;
                if (wsStatus === 'connected') {
                    setStatus('connected');
                } else if (wsStatus === 'disconnected') {
                    setStatus('disconnected');
                    setCollabReady(false);
                } else {
                    setStatus('connecting');
                }
            });

            // ── Sync status ──────────────────────────────────────────────────
            provider.on('sync', (isSynced) => {
                if (destroyed) return;
                if (isSynced) {
                    setCollabReady(true);
                }
            });

            // Failsafe: if no connection within 8s, mark as unavailable
            const fallbackTimer = setTimeout(() => {
                if (destroyed) return;
                if (provider.wsconnected === false) {
                    setStatus('unavailable');
                    setCollabReady(false);
                }
            }, 8000);

            // ── Awareness (presence) updates ──────────────────────────────────
            const updateCollaborators = () => {
                if (destroyed) return;
                const states = Array.from(provider.awareness.getStates().entries());
                const peers = states
                    .filter(([clientId, state]) => state?.user && clientId !== provider.awareness.clientID && state.isEditing === true)
                    .map(([clientId, state]) => ({ clientId, ...state.user }));
                setCollaborators(peers);
            };

            provider.awareness.on('change', updateCollaborators);
            updateCollaborators(); // initial read

            return () => {
                destroyed = true;
                clearTimeout(fallbackTimer);
                provider.awareness.off('change', updateCollaborators);
                provider.destroy();
                ydoc.destroy();
                ydocRef.current = null;
                providerRef.current = null;
                setCollabReady(false);
                setStatus('disconnected');
                setCollaborators([]);
            };
        } catch (err) {
            console.warn('[Collaboration] Setup failed – falling back to standard editing mode.', err);
            setStatus('unavailable');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roomName, wsBaseUrl, user?.id]);

    useEffect(() => {
        const provider = providerRef.current;
        if (provider?.awareness) {
            provider.awareness.setLocalStateField('isEditing', !!isEditing);
        }
    }, [isEditing]);

    return {
        ydoc: ydocRef.current,
        provider: providerRef.current,
        status,         // 'connecting' | 'connected' | 'disconnected' | 'unavailable'
        collaborators,  // array of { clientId, name, color, initials, userId }
        collabReady,    // true only when WS is connected and ydoc is synced
    };
}
